import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import SidebarNew from './components/SidebarNew';
import TaskDrawer from './components/TaskDrawer';
import { TaskProvider } from './contexts/TaskContext';
import TopBar from './components/TopBar';
import SettingsPage from './components/SettingsPage';
import DashboardPage from './pages/DashboardPage';
import InboxPage from './pages/InboxPage';
import ListPage from './pages/ListPage';
import SpaceDashboardPage from './pages/SpaceDashboardPage';
import MyTasks from './pages/MyTasks';
import GoalsPage from './pages/GoalsPage';
import { useAuth } from '../../hooks/useAuth';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const TaskManagerApp = () => {
  const { user } = useAuth();

  // Estado para controlar el ancho del sidebar
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem('sidebarWidth');
    return savedWidth ? parseInt(savedWidth) : 260;
  });

  // Mobile sidebar toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  // Load collapsed preference from Firestore
  useEffect(() => {
    if (!user?.uid) return;
    getDoc(doc(db, 'users', user.uid)).then(snap => {
      if (snap.exists() && snap.data().taskSidebarCollapsed !== undefined) {
        setSidebarCollapsed(snap.data().taskSidebarCollapsed);
      }
    }).catch(() => {});
  }, [user?.uid]);

  const handleToggleCollapse = () => {
    const newVal = !sidebarCollapsed;
    setSidebarCollapsed(newVal);
    localStorage.setItem('sidebarCollapsed', String(newVal));
    if (user?.uid) {
      setDoc(doc(db, 'users', user.uid), { taskSidebarCollapsed: newVal }, { merge: true }).catch(() => {});
    }
  };

  // Guardar el ancho en localStorage cuando cambia
  useEffect(() => {
    localStorage.setItem('sidebarWidth', sidebarWidth);
  }, [sidebarWidth]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <TaskProvider>
      <div className="flex h-full bg-gray-50 overflow-hidden relative">
        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - hidden on mobile, shown as offcanvas when sidebarOpen */}
        <div className={`
          fixed inset-y-0 left-0 z-40 md:relative md:z-auto
          transform transition-all duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `} style={{ width: sidebarCollapsed ? 64 : (window.innerWidth < 768 ? 280 : sidebarWidth) }}>
          <SidebarNew
            width={sidebarWidth}
            setWidth={setSidebarWidth}
            onClose={() => setSidebarOpen(false)}
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleToggleCollapse}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <TopBar onMenuToggle={() => setSidebarOpen(true)} />
          <main className="flex-1 overflow-y-auto">
            <Routes>
              <Route index element={<DashboardPage />} />
              <Route path="inbox" element={<InboxPage />} />
              <Route path="mis-tareas" element={<MyTasks />} />
              <Route path="lista/:listId" element={<ListPage />} />
              <Route path="respuestas" element={<div className="p-4">Respuestas (Próximamente)</div>} />
              <Route path="comentarios" element={<div className="p-4">Comentarios (Próximamente)</div>} />
              <Route path="espacio/:spaceId" element={<SpaceDashboardPage />} />
              <Route path="metas" element={<GoalsPage />} />
              <Route path="ajustes" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="." replace />} />
            </Routes>
          </main>
        </div>

        {/* Task Drawer (Global) */}
        <TaskDrawer />
      </div>
    </TaskProvider>
  );
};

export default TaskManagerApp;
